<script setup lang="ts">
import ConfirmDlg from '@/components/confirm-dlg.vue'
import { Team, TeamCollection } from '@/models/Team'
import KpTextfield from '@/components/kp-textfield.vue'
import KpToolbar from '@/components/kp-toolbar.vue'
import { useToast } from '@/composables/toast'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { computed, onMounted, onUpdated, ref, type Ref } from 'vue'
import type { DataTableHeader } from 'vuetify'
import { updateTeamRequests, useTeamRequests } from '@/composables/team'
import { useAuthUser } from '@/helpers/globals'
import { errToStr } from '@/helpers/tools'
import { createModel } from '@kp/js-model'
import type { TeamMitglied } from '@/models/TeamMitglied'
import { instance as api } from '@/helpers/api'
import type { ItemSlotBase } from 'vuetify/lib/components/VDataTable/types.mjs'
import { useRoute } from 'vuetify/lib/composables/router.mjs'

const createDlg = ref(false)
const loading = ref(false)
const teamName = ref('')
const teams = new TeamCollection()
const teamToDestroy: Ref<Team | null> = ref(null)
const confirmDelDlg = ref(false)
const teamLoading = ref(true)

const benutzer = useAuthUser()
const hasRecords = computed(() => {
    return teams && teams.getModels().length > 0
})

const toast = useToast()
const router = useRouter()
const { t } = useI18n()
const teamRequests: Ref<TeamMitglied[]> = useTeamRequests()

const headers: DataTableHeader[] = [
    { title: t('name'), value: '$.name', sortable: true },
    { title: t('members'), value: 'members', sortable: true },
    { title: t('erstellt_von'), value: '$.ersteller_name', sortable: true },
    { value: 'action', align: 'end', width: '120px', sortable: false },
]

onMounted(() => {
    getTeams()
    updateTeamRequests()
})

onUpdated(() => {
    getTeams()
})

async function getTeams() {
    teamLoading.value = true
    try {
        await teams.query()
    } catch (res) {
        toast.add({ text: 'Fehler: ' + res, color: 'error' })
    } finally {
        teamLoading.value = false
    }
}

function showForm(item: Team) {
    router.push({ name: 'team_edit', params: { id: item.id } })
}

function onAdd() {
    teamName.value = ''
    createDlg.value = true
    teams.query()
}

function destroy(team: Team) {
    teamToDestroy.value = team
    confirmDelDlg.value = true
}

async function confirmDestroy() {
    if (teamToDestroy.value) {
        try {
            await teamToDestroy.value.destroy()
            getTeams()
        } catch (e) {
            toast.add({ text: errToStr(e), color: 'error' })
        }
    }
    confirmDelDlg.value = false
    teamToDestroy.value = null
}

async function onNewTeam() {
    if (teamName.value.trim().length < 1) {
        return
    }
    const team = createModel(Team, { name: teamName.value })
    try {
        await team.save()
        createDlg.value = false
        router.push({ name: 'team_edit', params: { id: team.id } })
    } catch (e) {
        toast.add({ text: errToStr(e), color: 'error' })
    }
}

function declineRequest(request: TeamMitglied) {
    answer(false, request)
}

function acceptRequest(request: TeamMitglied) {
    answer(true, request)
}

function onRowClicked(event: PointerEvent, row: ItemSlotBase<Team>) {
    showForm(row.item)
}

async function answer(join: boolean, request: TeamMitglied) {
    loading.value = true
    try {
        await api().post('/team/answerRequests', {
            team_id: request.team_id,
            benutzer_id: request.benutzer_id,
            join: join,
        })

        await getTeams()
        updateTeamRequests()
    } finally {
        loading.value = false
    }
}
const route = useRoute()
</script>

<template>
    <div v-if="route?.name === 'teams'">
        <KpToolbar :title="t('teams')" icon="fas fa-users">
            <template #controls>
                <v-btn
                    class="hidden-xs"
                    rounded="xl"
                    variant="flat"
                    color="subtleHint"
                    @click="onAdd"
                    prepend-icon="fas fa-plus"
                >
                    {{ t('team_add') }}
                </v-btn>
                <v-btn
                    class="hidden-sm-and-up"
                    rounded="xl"
                    variant="flat"
                    color="subtleHint"
                    @click="onAdd"
                    icon="fas fa-plus"
                >
                </v-btn>
            </template>
        </KpToolbar>
        <v-container fluid class="kp-large-container">
            <v-alert
                class="centered-card"
                v-for="req in teamRequests"
                :key="req.id || 'new'"
                color="success"
                border="start"
                variant="tonal"
                elevation="2"
                icon="fas fa-users"
            >
                <h3>Einladung</h3>
                <div>
                    <strong>{{ req.ersteller_name }}</strong> hat Sie in das Team
                    <strong>{{ req.team_name }}</strong> eingeladen.<br />Wollen Sie diesem
                    beitreten?
                </div>
                <div class="einladung-button-container">
                    <v-btn color="error" outlined @click="declineRequest(req)"> Ablehnen </v-btn>
                    <v-btn color="success" @click="acceptRequest(req)"> Beitreten </v-btn>
                </div>
            </v-alert>
            <v-alert
                v-if="teamRequests && teamRequests.length === 0 && !hasRecords && !teamLoading"
                border="start"
                color="grey-lighten-3 black--text"
            >
                {{ t('no_records') }}
            </v-alert>
            <v-data-table-server
                v-if="hasRecords || teamLoading"
                class="fixed-table row-pointer"
                :headers="headers"
                :items="teams ? teams.getModels() : []"
                :items-length="teams.getServerTotal()"
                item-key="id"
                :loading="teamLoading"
                :no-data-text="t('no_records')"
                disable-pagination
                hide-default-footer
                @click:row="onRowClicked"
            >
                <template v-slot:[`item.members`]="{ item }">
                    {{
                        (item.anzahl_mitglieder || 0) > 1
                            ? item.anzahl_mitglieder + ' ' + t('members')
                            : item.anzahl_mitglieder + ' ' + t('member')
                    }}
                </template>
                <template v-slot:[`item.action`]="{ item }">
                    <span v-if="item.ersteller_id === benutzer?.id">
                        <v-btn
                            class="mr-1"
                            size="x-small"
                            variant="text"
                            color="accent"
                            icon="fas fa-pen"
                            @click.stop="showForm(item)"
                        >
                        </v-btn>
                        <v-btn
                            class="mr-1"
                            size="x-small"
                            variant="text"
                            color="error"
                            icon="fas fa-trash"
                            @click.stop="destroy(item)"
                        >
                        </v-btn>
                    </span>
                </template>
            </v-data-table-server>
            <ConfirmDlg
                v-model="createDlg"
                :title="t('team_add')"
                :yes-text="t('create')"
                :no-text="t('cancel')"
                :close-on-yes="false"
                @yes="onNewTeam"
            >
                <v-card-text>
                    <kp-textfield v-model="teamName" :placeholder="t('team_name')" autofocus />
                </v-card-text>
            </ConfirmDlg>

            <ConfirmDlg
                v-model="confirmDelDlg"
                :title="t('bestaetigung')"
                :text="t('really_delete_rec')"
                @yes="confirmDestroy"
                @no="teamToDestroy = null"
            />
        </v-container>
    </div>
    <router-view v-else />
</template>

<style scoped>
.small-icon {
    font-size: 15px;
}
.fixed-table {
    :deep(table) {
        table-layout: fixed;
        width: 100%;
    }
}
.row-pointer {
    :deep(tbody tr :hover) {
        cursor: pointer;
    }
}
@media screen and (min-width: 600px) {
    .fixed-table {
        :deep(table td) {
            text-overflow: ellipsis;
        }
    }
    .fixed-overflow-table {
        :deep(table td) {
            overflow: hidden;
        }
    }
}
@media screen and (max-width: 600px) {
    .fixed-table {
        :deep(table th) {
            width: 100%;
        }
    }
}
.einladung-button-container {
    margin-top: 1rem;
    display: flex;
    justify-content: space-between;
}
</style>
